// src/contexts/WorkHoursContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { doc, setDoc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useAuth } from "./AuthContext"; // Assuming you use AuthContext

// Create the Context
export const WorkHoursContext = createContext({
  isRunning: false,
  handleStart: () => {}, // Function to start the timer
});

// Helper for date formatting
const dateKeyFromDate = (d) => {
  return d ? new Date(d).toISOString().split("T")[0] : null;
};

// Custom Hook to manage Work Hours Logic
export const useWorkHours = () => {
  const { userId } = useAuth();
  const [currentSession, setCurrentSession] = useState({
    running: false,
    start: null,
  });

  const getUserDoc = (collectionName, docId) => {
    if (!userId || !collectionName || !docId) return null;
    return doc(db, "users", userId, collectionName, docId);
  };

  // --- Firestore Listener (Simplified to just track running state) ---
  useEffect(() => {
    const currentRef = getUserDoc("workSessions", "current");
    if (!currentRef) return;
    const unsub = onSnapshot(currentRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.data();
        setCurrentSession({
          running: data.running ?? false,
          start: data.start ?? null,
        });
      } else {
        setCurrentSession({ running: false, start: null });
      }
    });

    return () => unsub();
  }, [userId]);

  // --- START Function ---
  const handleStart = async () => {
    if (!userId) return;
    const now = Date.now();
    const sessionDoc = getUserDoc("workSessions", "current");
    if (!sessionDoc) return;

    // This is the core action: sets 'running: true' and 'start: now'
    await setDoc(sessionDoc, { running: true, start: now }, { merge: true });

    // Optional: Log the day start in 'workHours' collection to ensure the day is marked
    const todayKey = dateKeyFromDate(now);
    const hoursRef = getUserDoc("workHours", todayKey);
    await setDoc(hoursRef, { seconds: 0 }, { merge: true }); // Ensure doc exists
  };

  // NOTE: We don't need handleStop or the ticker here, as they are managed by WorkHoursTracker.jsx.
  // We only need the start action and the running state.

  return {
    isRunning: currentSession.running,
    handleStart,
  };
};

// Provider Component
export const WorkHoursProvider = ({ children }) => {
  const workHours = useWorkHours();

  return (
    <WorkHoursContext.Provider value={workHours}>
      {children}
    </WorkHoursContext.Provider>
  );
};

// Custom hook for convenience
export const useWorkHoursContext = () => useContext(WorkHoursContext);
